/**
 * UnifiedCallHub
 * ==============
 * Single draggable floating panel that is the sole calling widget in the app.
 * Replaces UnifiedCallWidget + DraggableCallWidget.
 *
 * Tabs:
 *  AI Agent    — Vapi AI voice agent: animated avatar, state label, live
 *                scrolling transcript, "Take Over Call" escalation button.
 *  Direct Call — WebRTC audio/video call: mute, camera, end, REC indicator.
 *
 * Architecture:
 *  • VapiContext      → AI agent state machine, transcript, isTalking
 *  • CallingContext   → WebRTC state (falls back to useWebRTC if no provider)
 *  • ReactDOM.createPortal → renders into document.body, escaping all MUI
 *                            stacking contexts so position:fixed works correctly
 *  • react-draggable  → drag handle, nodeRef (Strict Mode safe)
 *
 * Visibility:
 *  Shown when the Vapi AI call is loading/active OR when a WebRTC call is not
 *  idle.  Auto-dismisses 8 s after the WebRTC call ends.
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import ReactDOM from 'react-dom';
import Draggable from 'react-draggable';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CallEnd,
  Close,
  DragIndicator,
  Mic,
  MicOff,
  PersonOutline,
  SmartToy,
  SwapHoriz,
  Videocam,
  VideocamOff,
} from '@mui/icons-material';

import { useVapi, VoiceAgentState } from '../../contexts/VapiContext';
import { NeuralAvatarStream } from '../Avatar/NeuralAvatarStream';
import { AvatarErrorBoundary } from '../Avatar/AvatarErrorBoundary';
import type { NeuralAvatarHandle } from '../Avatar/NeuralAvatar';
import { useCallingContext } from './CallingContext';
import { useWebRTC } from '../../hooks/useWebRTC';
import {
  CallState,
  formatCallState,
  formatWebRTCError,
} from '../../utils/webrtc-config';
import { useTranscription } from '../../hooks/useTranscription';

// ─── Types ────────────────────────────────────────────────────────────────────

type HubTab = 'ai' | 'direct';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTime = (s: number) =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

// ─── Component ────────────────────────────────────────────────────────────────

export const UnifiedCallHub: React.FC = () => {
  const DraggableComponent = Draggable as unknown as React.ComponentType<any>;
  // ── Vapi (AI agent) ────────────────────────────────────────────────────────
  const {
    callStatus,
    agentState,
    isTalking,
    transcript,
    agentCaption,
    wlWsState,
    startCall:       startVapi,
    endCall:         endVapi,
    initiateHandover,
    lastHandoverContext,
    hubOpen,
    closeHub,
  } = useVapi();

  // ── WebRTC (direct call) ───────────────────────────────────────────────────
  const sharedCtx = useCallingContext();
  const ownWebrtc = useWebRTC({ autoConnect: sharedCtx === null, enableVideo: true });
  const webCtx    = sharedCtx ?? ownWebrtc;

  const {
    callState,
    localStream,
    remoteStream,
    isMuted,
    isVideoEnabled,
    sessionId,
    socket,
    incomingCallData,
    toggleMute,
    toggleVideo,
    endCall: endWebrtcCall,
    error,
    isRecording,
    myCaption,
    remoteCaption,
  } = webCtx;

  // ── Post-call transcript ───────────────────────────────────────────────────
  const isWebrtcEnded = callState === CallState.ENDED;
  const { fullTranscript } = useTranscription({
    remoteStream,
    socket,
    sessionId,
    enabled: callState === CallState.CONNECTED || isWebrtcEnded,
  });

  // ── NeuralAvatar: imperative handle for pushing audio / text to backend ─────
  const neuralAvatarRef      = useRef<NeuralAvatarHandle>(null);
  const lastTranscriptLenRef = useRef(0);

  // ── AI call recording ─────────────────────────────────────────────────────
  const aiRecorderRef  = useRef<MediaRecorder | null>(null);
  const aiChunksRef    = useRef<Blob[]>([]);
  const aiAudioCtxRef  = useRef<AudioContext | null>(null);

  // ── Local state ────────────────────────────────────────────────────────────
  const [tab,            setTab]           = useState<HubTab>('ai');
  const [dismissed,      setDismissed]     = useState(false);
  const [elapsed,        setElapsed]       = useState(0);
  const [startTime,      setStartTime]     = useState<number | null>(null);
  const [agentIsTalking, setAgentIsTalking] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const nodeRef          = useRef<HTMLDivElement>(null);
  const localVideoRef    = useRef<HTMLVideoElement>(null);
  const remoteVideoRef   = useRef<HTMLVideoElement>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // ── Derived flags ──────────────────────────────────────────────────────────
  const isVapiActive   = callStatus === 'active' || callStatus === 'loading';
  const isWebrtcActive = ![CallState.IDLE, CallState.ENDED, CallState.ERROR].includes(callState as CallState);
  const isRinging      = callState === CallState.RINGING && incomingCallData !== null;
  const isConnected    = callState === CallState.CONNECTED;
  const isPending      = [CallState.INITIATING, CallState.RINGING, CallState.CONNECTING].includes(callState as CallState);
  const isAudioOnly    = !isVideoEnabled && !remoteStream?.getVideoTracks().length;

  // ── Auto-switch tab based on activity ─────────────────────────────────────
  useEffect(() => {
    if (isWebrtcActive) setTab('direct');
    else if (isVapiActive) setTab('ai');
  }, [isWebrtcActive, isVapiActive]);

  // ── Reset dismissed when a new call starts ─────────────────────────────────
  useEffect(() => {
    if (isVapiActive || isWebrtcActive) setDismissed(false);
  }, [isVapiActive, isWebrtcActive]);

  // ── WebRTC call timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (callState === CallState.CONNECTED && startTime === null) {
      setStartTime(Date.now());
    } else if (callState !== CallState.CONNECTED) {
      setStartTime(null);
      setElapsed(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState]);

  useEffect(() => {
    if (startTime === null) return;
    const id = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - startTime!) / 1000)),
      1000,
    );
    return () => window.clearInterval(id);
  }, [startTime]);

  // ── Auto-dismiss after WebRTC call ends ───────────────────────────────────
  useEffect(() => {
    if (!isWebrtcEnded) return;
    const t = window.setTimeout(() => { setDismissed(true); closeHub(); }, 8_000);
    return () => window.clearTimeout(t);
  }, [isWebrtcEnded, closeHub]);

  // ── Scroll live transcript to bottom ──────────────────────────────────────
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  // ── Pipe AI speech text to NeuralAvatar → backend TTS → Wav2Lip ───────────
  // When a new assistant transcript entry arrives, send it so the backend can
  // synthesize speech audio and drive the lip-sync animation.
  useEffect(() => {
    if (transcript.length <= lastTranscriptLenRef.current) return;
    const newEntries = transcript.slice(lastTranscriptLenRef.current);
    lastTranscriptLenRef.current = transcript.length;
    const text = newEntries
      .filter(e => e.role === 'assistant')
      .map(e => e.text)
      .join(' ')
      .trim();
    if (text) neuralAvatarRef.current?.sendText(text);
  }, [transcript]);

  // ── AI call recorder: mix mic + avatar TTS audio into a single WebM file ──
  useEffect(() => {
    if (callStatus !== 'active') {
      // Stop and save any in-progress recording when the call ends
      if (aiRecorderRef.current && aiRecorderRef.current.state !== 'inactive') {
        aiRecorderRef.current.stop();
      }
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) { micStream.getTracks().forEach(t => t.stop()); return; }

        const avatarStream = neuralAvatarRef.current?.getAudioStream();

        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        const ctx: AudioContext = new AudioCtx();
        aiAudioCtxRef.current = ctx;
        const dest = ctx.createMediaStreamDestination();

        ctx.createMediaStreamSource(micStream).connect(dest);
        if (avatarStream) ctx.createMediaStreamSource(avatarStream).connect(dest);

        const recorder = new MediaRecorder(dest.stream, { mimeType: 'audio/webm' });
        aiRecorderRef.current = recorder;
        aiChunksRef.current   = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) aiChunksRef.current.push(e.data);
        };

        recorder.onstop = () => {
          micStream.getTracks().forEach(t => t.stop());
          ctx.close().catch(() => {});
          aiAudioCtxRef.current = null;
          const blob = new Blob(aiChunksRef.current, { type: 'audio/webm' });
          aiChunksRef.current = [];
          if (blob.size > 0) {
            const url = URL.createObjectURL(blob);
            const a   = document.createElement('a');
            a.href    = url;
            a.download = `ai_call_${Date.now()}.webm`;
            a.click();
            URL.revokeObjectURL(url);
          }
        };

        recorder.start(1000);
      } catch (err) {
        console.warn('[UnifiedCallHub] AI recording failed to start:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus]);

  // ── Video element srcObject ────────────────────────────────────────────────
  useEffect(() => {
    if (localVideoRef.current) localVideoRef.current.srcObject = localStream;
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
  }, [remoteStream]);

  // ── Agent mic RMS (isTalking flag for NeuralAvatar agentTalking prop) ───────
  const agentRmsRef = useRef<{ ctx: AudioContext; raf: number } | null>(null);
  useEffect(() => {
    if (!localStream || !isConnected) {
      if (agentRmsRef.current) {
        cancelAnimationFrame(agentRmsRef.current.raf);
        agentRmsRef.current.ctx.close().catch(() => {});
        agentRmsRef.current = null;
      }
      setAgentIsTalking(false);
      return;
    }
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.4;
    ctx.createMediaStreamSource(localStream).connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    let rafId: number;
    const tick = () => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
      setAgentIsTalking(Math.sqrt(sum / buf.length) > 0.02);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    agentRmsRef.current = { ctx, raf: rafId };
    return () => {
      cancelAnimationFrame(rafId);
      ctx.close().catch(() => {});
      agentRmsRef.current = null;
    };
  }, [localStream, isConnected]);

  // ── Functional handover (FR-H002): mute AI, unmute agent, capture context ─
  const handleTakeOver = useCallback(() => {
    initiateHandover('Agent requested takeover');
    setTab('direct');
    // Unmute agent's mic if it was muted so they can speak immediately
    if (isMuted) toggleMute();
  }, [initiateHandover, isMuted, toggleMute]);

  // ── Avatar helpers ─────────────────────────────────────────────────────────
  const stateLabel = (() => {
    switch (agentState) {
      case VoiceAgentState.SPEAKING:           return '● SPEAKING';
      case VoiceAgentState.LISTENING:          return '○ LISTENING';
      case VoiceAgentState.PROCESSING:         return '◌ PROCESSING';
      case VoiceAgentState.GREETING:           return '◉ GREETING';
      case VoiceAgentState.TRANSFERRING:       return '⟳ TRANSFERRING';
      case VoiceAgentState.WAITING_FOR_AGENT:  return '⏳ FINDING AGENT';
      case VoiceAgentState.ENDED:              return '✓ CALL ENDED';
      default:                                  return '○ IDLE';
    }
  })();

  const stateColor = (() => {
    switch (agentState) {
      case VoiceAgentState.SPEAKING:          return '#1f93ff';
      case VoiceAgentState.PROCESSING:        return '#ff9800';
      case VoiceAgentState.TRANSFERRING:      return '#f44336';
      case VoiceAgentState.GREETING:          return '#4caf50';
      case VoiceAgentState.WAITING_FOR_AGENT: return '#9c27b0';
      case VoiceAgentState.ENDED:             return '#4caf50';
      default:                                 return '#616161';
    }
  })();

  const avatarBorder = isTalking
    ? '#1f93ff'
    : agentState === VoiceAgentState.PROCESSING
    ? '#ff9800'
    : agentState === VoiceAgentState.WAITING_FOR_AGENT
    ? '#9c27b0'
    : 'rgba(255,255,255,0.1)';

  const avatarAnimation = isTalking
    ? 'hub-pulse 1.2s ease-out infinite'
    : agentState === VoiceAgentState.PROCESSING || agentState === VoiceAgentState.WAITING_FOR_AGENT
    ? 'hub-think 1s ease-in-out infinite'
    : 'none';

  // ── Visibility guard ───────────────────────────────────────────────────────
  const shouldShow =
    !dismissed &&
    !isRinging &&
    (isVapiActive || isWebrtcActive || isWebrtcEnded || hubOpen);

  if (!shouldShow) return null;

  // ─────────────────────────────────────────────────────────────────────────
  // Widget JSX
  // ─────────────────────────────────────────────────────────────────────────

  const widget = (
    <>
      {/* Keyframe animations — embedded so no external CSS file is required */}
      <style>{`
        @keyframes hub-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(31,147,255,0.75); }
          70%  { box-shadow: 0 0 0 18px rgba(31,147,255,0);   }
          100% { box-shadow: 0 0 0 0   rgba(31,147,255,0);   }
        }
        @keyframes hub-think {
          0%, 100% { opacity: 0.6; }
          50%       { opacity: 1;  }
        }
        @keyframes hub-rec {
          0%, 100% { opacity: 1;   }
          50%       { opacity: 0.3; }
        }
        @keyframes hub-photo-pulse {
          from { filter: brightness(1);    }
          to   { filter: brightness(1.22); }
        }
        @keyframes hub-mouth {
          from { transform: translateX(-50%) scaleY(0.5); opacity: 0.15; }
          to   { transform: translateX(-50%) scaleY(1);   opacity: 0.35; }
        }
      `}</style>

      <DraggableComponent handle=".hub-drag-handle" bounds="body" nodeRef={nodeRef}>
        <div
          ref={nodeRef}
          style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 99999, width: 360 }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#1a1a2e',
              outline: isTalking && tab === 'ai'
                ? '2px solid rgba(31,147,255,0.8)'
                : '2px solid transparent',
              transition: 'outline 0.2s ease',
            }}
          >
            {/* ── HEADER: drag handle + tab toggle + status ─────────────── */}
            <Box
              className="hub-drag-handle"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                bgcolor: '#0d0d1a',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                userSelect: 'none',
              }}
            >
              <DragIndicator sx={{ fontSize: 14, color: 'grey.700', flexShrink: 0 }} />

              {/* Tab toggle */}
              <ButtonGroup size="small" disableElevation>
                <Tooltip title="AI Agent voice call">
                  <Button
                    variant={tab === 'ai' ? 'contained' : 'outlined'}
                    onClick={() => setTab('ai')}
                    sx={{
                      fontSize: '0.6rem', px: 1, py: 0.25, minWidth: 0,
                      borderColor: 'rgba(255,255,255,0.15)',
                      color: tab === 'ai' ? 'white' : 'grey.500',
                      bgcolor: tab === 'ai' ? '#1f93ff' : 'transparent',
                      '&:hover': { bgcolor: tab === 'ai' ? '#1a7fdb' : 'rgba(255,255,255,0.05)' },
                    }}
                  >
                    <SmartToy sx={{ fontSize: 12, mr: 0.5 }} />
                    AI Agent
                  </Button>
                </Tooltip>
                <Tooltip title="Direct WebRTC call">
                  <Button
                    variant={tab === 'direct' ? 'contained' : 'outlined'}
                    onClick={() => setTab('direct')}
                    sx={{
                      fontSize: '0.6rem', px: 1, py: 0.25, minWidth: 0,
                      borderColor: 'rgba(255,255,255,0.15)',
                      color: tab === 'direct' ? 'white' : 'grey.500',
                      bgcolor: tab === 'direct' ? '#1f93ff' : 'transparent',
                      '&:hover': { bgcolor: tab === 'direct' ? '#1a7fdb' : 'rgba(255,255,255,0.05)' },
                    }}
                  >
                    <Mic sx={{ fontSize: 12, mr: 0.5 }} />
                    Direct Call
                  </Button>
                </Tooltip>
              </ButtonGroup>

              <Box sx={{ flexGrow: 1 }} />

              {/* Status indicators */}
              {isVapiActive && tab === 'ai' && (
                <Chip
                  label={callStatus === 'loading' ? 'Connecting…' : agentState}
                  size="small"
                  sx={{ height: 18, fontSize: '0.55rem', bgcolor: 'rgba(31,147,255,0.15)', color: '#1f93ff' }}
                />
              )}
              {isConnected && tab === 'direct' && (
                <Typography
                  variant="caption"
                  sx={{ color: '#4caf50', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.65rem' }}
                >
                  {formatTime(elapsed)}
                </Typography>
              )}
            </Box>

            {/* ═══════════════════════════════════════════════════════════════
                AI AGENT TAB
            ═══════════════════════════════════════════════════════════════ */}
            {tab === 'ai' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#0d0d1a' }}>
                {/* AI Avatar — animated SVG character, always shown in AI tab */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    bgcolor: '#0a0a18',
                    border: '2px solid',
                    borderColor: avatarBorder,
                    borderRadius: 1,
                    overflow: 'hidden',
                    transition: 'border-color 0.15s ease',
                    animation: avatarAnimation,
                    mx: 0,
                    mt: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <AvatarErrorBoundary>
                      <NeuralAvatarStream
                        ref={neuralAvatarRef}
                        active={isVapiActive}
                        width={340}
                        height={200}
                        agentTalking={agentIsTalking || agentState === VoiceAgentState.SPEAKING}
                      />
                    </AvatarErrorBoundary>
                  </Box>

                  {/* State label — overlaid top-left */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 10,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: stateColor, fontWeight: 700, letterSpacing: '0.08em', transition: 'color 0.15s ease' }}
                    >
                      {stateLabel}
                    </Typography>
                  </Box>

                  {/* WL transcript caption — overlaid bottom */}
                  {agentCaption && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'rgba(0,0,0,0.72)',
                        px: 1.5,
                        py: 0.75,
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'grey.200', lineHeight: 1.5, wordBreak: 'break-word' }}>
                        {agentCaption}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Live scrolling transcript panel */}
                {isVapiActive && transcript.length > 0 && (
                  <Box
                    sx={{
                      mx: 2,
                      mb: 1.5,
                      maxHeight: 130,
                      overflowY: 'auto',
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 1,
                      '&::-webkit-scrollbar': { width: 4 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: 'grey.600', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}
                    >
                      LIVE TRANSCRIPT
                    </Typography>
                    {transcript.map((entry, i) => (
                      <Typography
                        key={i}
                        variant="caption"
                        component="div"
                        sx={{ lineHeight: 1.6, mb: 0.25 }}
                      >
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: entry.role === 'assistant' ? '#7c9eff' : '#4caf50' }}
                        >
                          {entry.role === 'assistant' ? 'AI: ' : 'User: '}
                        </Box>
                        <Box component="span" sx={{ color: entry.role === 'assistant' ? '#bcd4ff' : 'grey.300' }}>
                          {entry.text}
                        </Box>
                      </Typography>
                    ))}
                    {/* Scroll anchor */}
                    <div ref={transcriptEndRef} />
                  </Box>
                )}

                {/* Reconnecting banner — WhisperLivekit or Vapi WS dropped */}
                {(wlWsState === 'reconnecting' || wlWsState === 'error') && (
                  <Box sx={{ mx: 2, mb: 1, px: 1.5, py: 0.5, bgcolor: wlWsState === 'error' ? 'error.dark' : 'warning.dark', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                      {wlWsState === 'reconnecting' ? '⟳ Reconnecting transcription…' : '⚠ Transcription unavailable'}
                    </Typography>
                  </Box>
                )}

                {/* Controls row */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', pb: 2, px: 2, flexWrap: 'wrap', mt: 1 }}>
                  {/* Take Over Call — visible when AI is active (FR-H002) */}
                  {isVapiActive && (
                    <Tooltip title="Take Over Call — stop AI and join the call directly">
                      <Button
                        onClick={handleTakeOver}
                        size="small"
                        variant="outlined"
                        startIcon={<SwapHoriz fontSize="small" />}
                        sx={{
                          fontSize: '0.65rem',
                          color: '#ff9800',
                          borderColor: '#ff9800',
                          '&:hover': { bgcolor: 'rgba(255,152,0,0.1)', borderColor: '#ff9800' },
                        }}
                      >
                        Take Over Call
                      </Button>
                    </Tooltip>
                  )}

                  {isVapiActive ? (
                    /* End AI call */
                    <Tooltip title="End AI call">
                      <IconButton
                        onClick={endVapi}
                        size="medium"
                        sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 48, height: 48 }}
                      >
                        <CallEnd />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    /* Start AI call (callStatus is 'idle' here — loading shows End button above) */
                    <Tooltip title="Start AI Agent call">
                      <Button
                        onClick={startVapi}
                        variant="contained"
                        startIcon={<SmartToy />}
                        sx={{ bgcolor: '#1f93ff', '&:hover': { bgcolor: '#1a7fdb' } }}
                      >
                        Start AI Call
                      </Button>
                    </Tooltip>
                  )}

                  <Tooltip title="Close">
                    <IconButton onClick={() => { setDismissed(true); closeHub(); }} size="small" sx={{ color: 'grey.600' }}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                DIRECT CALL TAB (WebRTC)
            ═══════════════════════════════════════════════════════════════ */}
            {tab === 'direct' && (
              <>
                {/* ── FR-H003: AI handover context panel ─────────────────── */}
                {lastHandoverContext && (
                  <Box
                    sx={{
                      mx: 1.5, mt: 1.5, mb: 0.5,
                      bgcolor: 'rgba(156,39,176,0.1)',
                      border: '1px solid rgba(156,39,176,0.3)',
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#ce93d8', letterSpacing: '0.06em', fontWeight: 700, display: 'block', mb: 0.75 }}>
                      📋 INTERNAL NOTES — AI HANDOVER
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: 'grey.400' }}>
                        AI turns: <Box component="span" sx={{ color: 'white', fontWeight: 600 }}>{lastHandoverContext.aiTurns}</Box>
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'grey.400' }}>
                        Reason: <Box component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>{lastHandoverContext.escalationReason}</Box>
                      </Typography>
                    </Box>
                    {lastHandoverContext.lastCaption && (
                      <Typography variant="caption" sx={{ color: 'grey.300', display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                        Last AI: "{lastHandoverContext.lastCaption}"
                      </Typography>
                    )}
                    {lastHandoverContext.transcript.length > 0 && (
                      <Box sx={{ maxHeight: 80, overflowY: 'auto', mt: 0.5, '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                        {lastHandoverContext.transcript.slice(-5).map((e, i) => (
                          <Typography key={i} variant="caption" component="div" sx={{ lineHeight: 1.5, color: e.role === 'assistant' ? '#bcd4ff' : 'grey.300' }}>
                            <Box component="span" sx={{ fontWeight: 700, color: e.role === 'assistant' ? '#7c9eff' : '#4caf50' }}>
                              {e.role === 'assistant' ? 'AI: ' : 'User: '}
                            </Box>
                            {e.text}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Call ended — show transcript then auto-dismiss */}
                {isWebrtcEnded ? (
                  <Box>
                    <Box sx={{ px: 1.5, py: 1, bgcolor: '#0d0d1a', maxHeight: 160, overflowY: 'auto' }}>
                      <Typography variant="caption" sx={{ color: 'grey.600', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
                        CALL TRANSCRIPT
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: fullTranscript ? 'grey.300' : 'grey.700',
                          fontStyle: fullTranscript ? 'normal' : 'italic',
                          fontFamily: 'monospace',
                          lineHeight: 1.6,
                        }}
                      >
                        {fullTranscript || 'Transcribing…'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5, py: 0.75, bgcolor: '#16213e' }}>
                      <Tooltip title="Close">
                        <IconButton onClick={() => { setDismissed(true); closeHub(); }} size="small" sx={{ color: 'grey.500' }}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : !isWebrtcActive ? (
                  /* No active WebRTC call */
                  <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#0d0d1a', gap: 1.5 }}>
                    <PersonOutline sx={{ fontSize: 48, color: 'grey.700' }} />
                    <Typography variant="body2" sx={{ color: 'grey.500' }}>No active direct call</Typography>
                    <Tooltip title="Close">
                      <IconButton onClick={() => { setDismissed(true); closeHub(); }} size="small" sx={{ color: 'grey.600' }}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : isAudioOnly ? (
                  /* ── Audio-only call ── */
                  <>
                    <Box sx={{ bgcolor: '#0d0d1a', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'success.dark', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PersonOutline sx={{ fontSize: 32, color: 'white' }} />
                      </Box>
                      <Typography variant="body2" color="white" fontWeight={600}>
                        {isPending ? formatCallState(callState) : 'Call Active'}
                      </Typography>
                      {isConnected && (
                        <Typography variant="h6" sx={{ color: '#2e7d32', fontFamily: 'monospace', fontWeight: 700 }}>
                          {formatTime(elapsed)}
                        </Typography>
                      )}
                      {isConnected && (myCaption || remoteCaption) && (
                        <Box sx={{ width: '100%', bgcolor: 'rgba(0,0,0,0.7)', borderRadius: 1, px: 1.5, py: 0.75, mt: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#aaa', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
                            LIVE CAPTIONS
                          </Typography>
                          {myCaption && (
                            <Typography variant="caption" sx={{ color: 'white', lineHeight: 1.5, display: 'block', wordBreak: 'break-word', mb: remoteCaption ? 0.5 : 0 }}>
                              <Box component="span" sx={{ color: '#7c9eff', fontWeight: 700 }}>You: </Box>
                              {myCaption}
                            </Typography>
                          )}
                          {remoteCaption && (
                            <Typography variant="caption" sx={{ color: 'white', lineHeight: 1.5, display: 'block', wordBreak: 'break-word' }}>
                              <Box component="span" sx={{ color: '#4caf50', fontWeight: 700 }}>Them: </Box>
                              {remoteCaption}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#16213e' }}>
                      <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
                        <IconButton onClick={toggleMute} size="small" color={!isMuted ? 'primary' : 'default'} sx={{ width: 40, height: 40 }}>
                          {isMuted ? <MicOff fontSize="small" /> : <Mic fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}>
                        <IconButton onClick={toggleVideo} size="small" color={isVideoEnabled ? 'primary' : 'default'} sx={{ width: 40, height: 40 }}>
                          {isVideoEnabled ? <Videocam fontSize="small" /> : <VideocamOff fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="End call">
                        <IconButton onClick={endWebrtcCall} size="medium" color="error" sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 48, height: 48 }}>
                          <CallEnd />
                        </IconButton>
                      </Tooltip>
                      {isRecording && (
                        <Chip label="REC" size="small" sx={{ bgcolor: 'error.main', color: 'white', fontWeight: 700, fontSize: 10, height: 20, letterSpacing: '0.08em', animation: 'hub-rec 1.4s ease-in-out infinite' }} />
                      )}
                      <Tooltip title="Close widget">
                        <IconButton onClick={() => { setDismissed(true); closeHub(); }} size="small" sx={{ color: 'grey.500', ml: 0.5 }}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {(isPending || error) && (
                      <Box sx={{ px: 1.5, py: 0.75, bgcolor: error ? 'error.main' : 'warning.main', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: error ? 'white' : 'warning.contrastText', fontWeight: 500 }}>
                          {error ? formatWebRTCError(error) : formatCallState(callState)}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  /* ── Video call ── */
                  <>
                    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9', bgcolor: '#0d0d1a' }}>
                      <video
                        ref={remoteVideoRef}
                        autoPlay
                        playsInline
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: isConnected && remoteStream ? 'block' : 'none' }}
                      />
                      {(!isConnected || !remoteStream) && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <PersonOutline sx={{ fontSize: 56, color: 'grey.600' }} />
                          <Typography variant="caption" color="grey.500">
                            {isPending ? formatCallState(callState) : 'No video'}
                          </Typography>
                        </Box>
                      )}
                      {isConnected && (
                        <Chip label={formatTime(elapsed)} size="small" sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.55)', color: 'white', fontFamily: 'monospace', fontSize: 12, height: 22 }} />
                      )}
                      {isConnected && (myCaption || remoteCaption) && (
                        <Box sx={{ position: 'absolute', bottom: 10, left: 10, right: 108, bgcolor: 'rgba(0,0,0,0.7)', borderRadius: 1, px: 1.5, py: 0.75, pointerEvents: 'none' }}>
                          {myCaption && (
                            <Typography variant="caption" sx={{ color: 'white', lineHeight: 1.5, display: 'block', wordBreak: 'break-word', mb: remoteCaption ? 0.5 : 0 }}>
                              <Box component="span" sx={{ color: '#7c9eff', fontWeight: 700 }}>You: </Box>{myCaption}
                            </Typography>
                          )}
                          {remoteCaption && (
                            <Typography variant="caption" sx={{ color: 'white', lineHeight: 1.5, display: 'block', wordBreak: 'break-word' }}>
                              <Box component="span" sx={{ color: '#4caf50', fontWeight: 700 }}>Them: </Box>{remoteCaption}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {/* Local PiP */}
                      <Box sx={{ position: 'absolute', bottom: 10, right: 10, width: 88, height: 66, borderRadius: 1, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.25)', bgcolor: '#111' }}>
                        <video
                          ref={localVideoRef}
                          autoPlay
                          playsInline
                          muted
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: localStream && isVideoEnabled ? 'block' : 'none', transform: 'scaleX(-1)' }}
                        />
                        {(!localStream || !isVideoEnabled) && (
                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Mic sx={{ fontSize: 20, color: 'grey.600' }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 2, py: 1.5, bgcolor: '#16213e' }}>
                      <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
                        <IconButton onClick={toggleMute} size="small" color={!isMuted ? 'primary' : 'default'} sx={{ width: 40, height: 40 }}>
                          {isMuted ? <MicOff fontSize="small" /> : <Mic fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}>
                        <IconButton onClick={toggleVideo} size="small" color={isVideoEnabled ? 'primary' : 'default'} sx={{ width: 40, height: 40 }}>
                          {isVideoEnabled ? <Videocam fontSize="small" /> : <VideocamOff fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="End call">
                        <IconButton onClick={endWebrtcCall} size="medium" color="error" sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 48, height: 48 }}>
                          <CallEnd />
                        </IconButton>
                      </Tooltip>
                      {isRecording && (
                        <Chip label="REC" size="small" sx={{ bgcolor: 'error.main', color: 'white', fontWeight: 700, fontSize: 10, height: 20, letterSpacing: '0.08em', animation: 'hub-rec 1.4s ease-in-out infinite' }} />
                      )}
                      <Tooltip title="Close widget">
                        <IconButton onClick={() => { setDismissed(true); closeHub(); }} size="small" sx={{ color: 'grey.500', ml: 0.5 }}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {(isPending || error) && (
                      <Box sx={{ px: 1.5, py: 0.75, bgcolor: error ? 'error.main' : 'warning.main', textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: error ? 'white' : 'warning.contrastText', fontWeight: 500 }}>
                          {error ? formatWebRTCError(error) : formatCallState(callState)}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Paper>
        </div>
      </DraggableComponent>

      {/* Hidden audio element for remote stream (needed when video element is not rendered) */}
      {remoteStream && tab === 'direct' && (
        <audio
          autoPlay
          ref={el => { if (el) el.srcObject = remoteStream; }}
          style={{ display: 'none' }}
        />
      )}
    </>
  );

  // Render into document.body — escapes all MUI stacking contexts so
  // position:fixed is correctly relative to the viewport.
  return ReactDOM.createPortal(widget, document.body);
};

export default UnifiedCallHub;
